import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { formatRp, formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ArrowRight, User, User2 } from 'lucide-react';
import { cashierBadgeClass } from '@/components/CashierField';
import CashMovementDialog from '@/components/CashMovementDialog';
import AgiBalanceDialog from '@/components/AgiBalanceDialog';
import { useAuth } from '@/context/AuthContext';

export default function MutasiDanaSection({ pid, reloadKey }) {
  const { can } = useAuth();
  const canWrite = can('business.write');
  const canDelete = can('business.delete');
  const [data, setData] = useState(null);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [balanceOpen, setBalanceOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        api.get(`/projects/${pid}/cashiers`),
        api.get(`/projects/${pid}/cash-movements`),
      ]);
      setData(c.data);
      setMovements(
        m.data.filter(
          ({ from_cashier, to_cashier }) =>
            from_cashier !== 'Lainnya' && to_cashier !== 'Lainnya'
        )
      );
    } catch (e) { toast.error('Gagal memuat mutasi'); }
    finally { setLoading(false); }
  }, [pid]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const doDelete = async (m) => {
    try {
      await api.delete(`/cash-movements/${m.id}`);
      toast.success('Mutasi dihapus');
      load();
    } catch (e) { toast.error('Gagal menghapus'); }
  };

  if (loading || !data) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
      </div>
    );
  }

  const cashiers = data.cashiers.filter(({ cashier }) => cashier === 'Agi' || cashier === 'Albi');
  const iconMap = { Agi: User, Albi: User2 };

  return (
    <div className="space-y-4">
      {/* Cashier saldo cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cashiers.map((c) => {
          const Icon = iconMap[c.cashier] || User;
          const isNeg = c.balance < 0;
          const actual = c.cashier === 'Agi' ? data.agi_actual_balance : null;
          const actualTotal = actual?.amount;
          const difference = actualTotal === undefined || actualTotal === null ? null : actualTotal - c.balance;
          const differenceClass = difference === null
            ? 'text-subtle-fpccs'
            : difference < 0 ? 'text-[var(--fpccs-danger)]' : 'text-[var(--fpccs-success)]';
          return (
            <div key={c.cashier} data-testid={`cashier-card-${c.cashier}`} className="card-fpccs p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${cashierBadgeClass(c.cashier)}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{c.cashier}</div>
                    <div className="text-[10px] text-subtle-fpccs">{c.tx_count} transaksi</div>
                  </div>
                </div>
                <span className={`text-[11px] px-1.5 py-0.5 rounded border ${cashierBadgeClass(c.cashier)}`}>Kasir</span>
              </div>
              <div className={`mt-3 ${c.cashier === 'Agi' ? 'grid grid-cols-1 sm:grid-cols-2 gap-3 sm:items-end' : ''}`}>
                <div>
                  <div className="text-[11px] text-subtle-fpccs">Sisa Saldo Sistem</div>
                  <div className={`text-xl font-semibold font-mono ${isNeg ? 'text-[var(--fpccs-danger)]' : 'text-[var(--fpccs-text)]'}`}>{formatRp(c.balance)}</div>
                </div>
                {c.cashier === 'Agi' && (
                  <div className="sm:text-right">
                    <div className="flex items-center gap-1 sm:justify-end">
                      <span className="text-[11px] text-subtle-fpccs">Saldo Aktual</span>
                      {canWrite && <button
                        type="button"
                        data-testid="btn-edit-agi-balance"
                        onClick={() => setBalanceOpen(true)}
                        aria-label="Update saldo aktual Agi"
                        className="w-6 h-6 rounded-md border border-[var(--fpccs-border)] text-subtle-fpccs hover:text-[var(--fpccs-primary)] hover:border-[var(--fpccs-primary)] flex items-center justify-center"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>}
                    </div>
                    <div className="text-xl font-semibold font-mono">{actualTotal === undefined || actualTotal === null ? '-' : formatRp(actualTotal)}</div>
                    <div className={`text-xs font-mono ${differenceClass}`}>{difference === null ? 'Belum disinkronkan' : formatRp(difference)}</div>
                  </div>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                <div className="rounded bg-[var(--fpccs-surface-2)] p-1.5"><div className="text-subtle-fpccs">Diterima</div><div className="font-mono text-[var(--fpccs-success)]">{formatRp(c.total_income, { compact: true })}</div></div>
                <div className="rounded bg-[var(--fpccs-surface-2)] p-1.5"><div className="text-subtle-fpccs">Keluar</div><div className="font-mono text-[var(--fpccs-danger)]">{formatRp(c.total_expense, { compact: true })}</div></div>
                <div className="rounded bg-[var(--fpccs-surface-2)] p-1.5"><div className="text-subtle-fpccs">Trf Masuk</div><div className="font-mono text-[var(--fpccs-info)]">{formatRp(c.transfer_in, { compact: true })}</div></div>
                <div className="rounded bg-[var(--fpccs-surface-2)] p-1.5"><div className="text-subtle-fpccs">Trf Keluar</div><div className="font-mono text-[var(--fpccs-warning)]">{formatRp(c.transfer_out, { compact: true })}</div></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Per cashier category breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {cashiers.map((c) => (
          <div key={c.cashier} data-testid={`cashier-breakdown-${c.cashier}`} className="card-fpccs p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">Pengeluaran {c.cashier}</div>
              <span className="text-xs text-subtle-fpccs">Cash {formatRp(c.method_breakdown.Cash, { compact: true })} · Trf {formatRp(c.method_breakdown.Transfer, { compact: true })}</span>
            </div>
            {c.category_breakdown.length === 0 ? (
              <div className="text-xs text-subtle-fpccs py-4">Belum ada pengeluaran</div>
            ) : (
              <div className="space-y-1.5">
                {c.category_breakdown.map((cat, i) => {
                  const pct = c.total_expense > 0 ? (cat.total / c.total_expense * 100) : 0;
                  return (
                    <div key={i}>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-fpccs">{cat.category}</span>
                        <span className="font-mono">{formatRp(cat.total, { compact: true })}</span>
                      </div>
                      <div className="h-1 rounded bg-[var(--fpccs-surface-3)] overflow-hidden">
                        <div className="h-full bg-[var(--fpccs-primary)]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Cash Movements list */}
      <div className="card-fpccs p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold">Riwayat Mutasi Dana</div>
            <div className="text-[11px] text-subtle-fpccs">Perpindahan uang antar kasir / rekening utama</div>
          </div>
          {canWrite && <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="btn-add-cash-movement"
                  onClick={() => { setEditing(null); setOpen(true); }}
                  className="w-8 h-8 rounded-md border border-[rgba(var(--fpccs-primary-rgb),0.35)] bg-[rgba(var(--fpccs-primary-rgb),0.10)] text-[var(--fpccs-primary)] hover:bg-[rgba(var(--fpccs-primary-rgb),0.18)] flex items-center justify-center"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Catatan Transaksi</TooltipContent>
            </Tooltip>
          </TooltipProvider>}
        </div>

        {/* Tinggi area riwayat dibatasi kira-kira untuk header + 5 baris data.
            Riwayat berikutnya tetap bisa dibuka dengan scroll di area ini. */}
        <div
          data-testid="cash-movement-history-scroll"
          className="max-h-[300px] overflow-auto rounded-md border border-[var(--fpccs-border)]"
        >
          <Table>
            <TableHeader>
              <TableRow className="border-b border-[var(--fpccs-border)] hover:bg-transparent">
                <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Tanggal</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Dari</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs"></TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Ke</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Metode</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs text-right">Nominal</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Keterangan</TableHead>
                <TableHead className="w-24 text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {movements.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-fpccs py-10">Belum ada mutasi dana.</TableCell></TableRow>
              ) : movements.map((m) => (
                <TableRow key={m.id} data-testid={`movement-row-${m.id}`} className="border-b border-[var(--fpccs-border)] hover:bg-white/[0.03]">
                  <TableCell className="text-xs whitespace-nowrap">{formatDate(m.date)}</TableCell>
                  <TableCell><span className={`text-[11px] px-1.5 py-0.5 rounded border ${cashierBadgeClass(m.from_cashier)}`}>{m.from_cashier}</span></TableCell>
                  <TableCell><ArrowRight className="w-3.5 h-3.5 text-[var(--fpccs-primary)]" /></TableCell>
                  <TableCell><span className={`text-[11px] px-1.5 py-0.5 rounded border ${cashierBadgeClass(m.to_cashier)}`}>{m.to_cashier}</span></TableCell>
                  <TableCell className="text-xs text-muted-fpccs">{m.payment_method || '-'}</TableCell>
                  <TableCell className="text-xs text-right font-mono font-semibold">{formatRp(m.amount)}</TableCell>
                  <TableCell className="text-xs text-muted-fpccs max-w-[280px] truncate">{m.note}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {canWrite && <Button size="icon" variant="ghost" data-testid={`btn-edit-mv-${m.id}`} onClick={() => { setEditing(m); setOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button>}
                      {canDelete && <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" data-testid={`btn-delete-mv-${m.id}`} className="text-[var(--fpccs-danger)]"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Hapus mutasi ini?</AlertDialogTitle><AlertDialogDescription>Aksi ini tidak bisa dibatalkan.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => doDelete(m)} className="bg-[var(--fpccs-danger)] text-[var(--fpccs-bg)]">Hapus</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {canWrite && <CashMovementDialog
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}
        pid={pid}
        initial={editing}
        onSaved={load}
      />}
      {canWrite && <AgiBalanceDialog
        open={balanceOpen}
        onOpenChange={setBalanceOpen}
        pid={pid}
        systemBalance={(cashiers.find((c) => c.cashier === 'Agi') || {}).balance || 0}
        current={data.agi_actual_balance}
        onSaved={load}
      />}
    </div>
  );
}
